import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const Footer = () => {
    const { t } = useTranslation();
    return (
        <footer className="bg-stone-900 text-stone-300 border-t border-stone-800 transition-colors duration-300">
            {/* Top decorative reassurance bar */}
            <div className="bg-stone-950/60 border-b border-stone-800/80 py-4 px-6 text-center text-xs tracking-wider uppercase text-stone-400">
                <div className="container mx-auto flex flex-wrap items-center justify-center gap-4 sm:gap-8">
                    <span className="flex items-center">
                        <span className="w-1.5 h-1.5 bg-terracotta rounded-full mr-2"></span>
                        Colegiul Psihologilor din România
                    </span>
                    <span className="flex items-center">
                        <span className="w-1.5 h-1.5 bg-terracotta rounded-full mr-2"></span>
                        Societatea Internațională de Schema Therapy (ISST)
                    </span>
                    <span className="flex items-center">
                        <span className="w-1.5 h-1.5 bg-terracotta rounded-full mr-2"></span>
                        Cabinet Dorobanți & Consultații Online
                    </span>
                </div>
            </div>

            <div className="container mx-auto px-6 py-16">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-10 lg:gap-12 mb-12">
                    
                    {/* Brand & Bio Column */}
                    <div className="lg:col-span-5 space-y-4">
                        <div className="space-y-1">
                            <h3 className="font-display text-2xl sm:text-3xl font-bold text-white tracking-tight">
                                Oana Tenea
                            </h3>
                            <p className="text-terracotta font-medium text-sm sm:text-base">
                                {t('footer.subtitle')}
                            </p>
                        </div>
                        <p className="text-stone-400 text-sm leading-relaxed max-w-md">
                            {t('footer.description')}
                        </p>
                        <div className="pt-2 flex items-start text-stone-300 text-sm">
                            <svg className="w-5 h-5 mr-3 text-terracotta flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                            </svg>
                            <span>{t('footer.address')}</span>
                        </div>
                    </div>
                    
                    {/* Quick Navigation Links */}
                    <div className="lg:col-span-3">
                        <h4 className="text-xs font-semibold uppercase tracking-wider text-stone-400 mb-4">
                            {t('footer.quickLinks')}
                        </h4>
                        <ul className="space-y-2.5 text-sm">
                            <li>
                                <Link to="/" className="text-stone-300 hover:text-white transition-colors duration-200 flex items-center group">
                                    <span className="w-1 h-1 bg-terracotta rounded-full mr-2.5 group-hover:scale-150 transition-transform"></span>
                                    {t('footer.home')}
                                </Link>
                            </li>
                            <li>
                                <Link to="/despre" className="text-stone-300 hover:text-white transition-colors duration-200 flex items-center group">
                                    <span className="w-1 h-1 bg-terracotta rounded-full mr-2.5 group-hover:scale-150 transition-transform"></span>
                                    {t('footer.about')}
                                </Link>
                            </li>
                            <li>
                                <Link to="/servicii" className="text-stone-300 hover:text-white transition-colors duration-200 flex items-center group">
                                    <span className="w-1 h-1 bg-terracotta rounded-full mr-2.5 group-hover:scale-150 transition-transform"></span>
                                    {t('footer.services')}
                                </Link>
                            </li>
                            <li>
                                <Link to="/resurse" className="text-stone-300 hover:text-white transition-colors duration-200 flex items-center group">
                                    <span className="w-1 h-1 bg-terracotta rounded-full mr-2.5 group-hover:scale-150 transition-transform"></span>
                                    {t('footer.resources')}
                                </Link>
                            </li>
                            <li>
                                <Link to="/blog" className="text-stone-300 hover:text-white transition-colors duration-200 flex items-center group">
                                    <span className="w-1 h-1 bg-terracotta rounded-full mr-2.5 group-hover:scale-150 transition-transform"></span>
                                    {t('footer.blog')}
                                </Link>
                            </li>
                            <li>
                                <Link to="/contact" className="text-stone-300 hover:text-white transition-colors duration-200 flex items-center group">
                                    <span className="w-1 h-1 bg-terracotta rounded-full mr-2.5 group-hover:scale-150 transition-transform"></span>
                                    {t('footer.contact')}
                                </Link>
                            </li>
                        </ul>
                    </div>
                    
                    {/* Contact & Professional Connections */}
                    <div className="lg:col-span-4 space-y-4">
                        <h4 className="text-xs font-semibold uppercase tracking-wider text-stone-400 mb-4">
                            Contact & Rețele
                        </h4>
                        <div className="space-y-3 text-sm">
                            <div className="flex items-center text-stone-300">
                                <svg className="w-5 h-5 mr-3 text-terracotta flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                </svg>
                                <a href="mailto:psihoterapeut@oanatenea.ro" className="hover:text-white transition-colors underline-offset-4 hover:underline">
                                    psihoterapeut@oanatenea.ro
                                </a>
                            </div>

                            <div className="flex items-center text-stone-300">
                                <svg className="w-5 h-5 mr-3 text-terracotta flex-shrink-0" fill="currentColor" viewBox="0 0 24 24">
                                    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                                </svg>
                                <a 
                                    href="https://instagram.com/psihoterapeut.oanatenea" 
                                    target="_blank" 
                                    rel="noopener noreferrer" 
                                    className="hover:text-white transition-colors"
                                >
                                    @psihoterapeut.oanatenea
                                </a>
                            </div>

                            <div className="flex items-center gap-3 pt-2">
                                <a 
                                    href="https://www.facebook.com/PsihologOanaTenea/" 
                                    target="_blank" 
                                    rel="noopener noreferrer"
                                    aria-label="Facebook Psiholog Oana Tenea"
                                    className="w-9 h-9 rounded-full bg-stone-800 hover:bg-stone-700 flex items-center justify-center text-stone-300 hover:text-white transition-colors"
                                >
                                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                                        <path d="M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.988C18.343 21.128 22 16.991 22 12z"/>
                                    </svg>
                                </a>
                                <a 
                                    href="https://ro.linkedin.com/in/oana-tenea-7996633" 
                                    target="_blank" 
                                    rel="noopener noreferrer"
                                    aria-label="LinkedIn Oana Tenea"
                                    className="w-9 h-9 rounded-full bg-stone-800 hover:bg-stone-700 flex items-center justify-center text-stone-300 hover:text-white transition-colors"
                                >
                                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                                        <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z"/>
                                    </svg>
                                </a>
                                <a 
                                    href="https://smartliving.ro/author/oana-tenea/" 
                                    target="_blank" 
                                    rel="noopener noreferrer"
                                    className="text-xs px-3 py-1.5 rounded-full bg-stone-800 hover:bg-stone-700 text-stone-300 hover:text-white transition-colors flex items-center"
                                >
                                    Autor SmartLiving
                                    <svg className="w-3 h-3 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                    </svg>
                                </a>
                            </div>
                        </div>
                    </div>
                </div>
                
                {/* Bottom Section: Legal & Credits */}
                <div className="border-t border-stone-800 pt-8 flex flex-col md:flex-row justify-between items-center gap-6 text-xs text-stone-400">
                    <p>
                        &copy; {new Date().getFullYear()} Oana Tenea. {t('footer.rights')}
                    </p>

                    <div className="flex flex-wrap items-center gap-6">
                        <a 
                            href="https://reclamatiisal.anpc.ro/" 
                            target="_blank" 
                            rel="noopener noreferrer" 
                            className="hover:opacity-90 transition-opacity"
                        >
                            <img 
                                src="/anpc-sal.webp" 
                                alt="ANPC SAL - Soluționarea Alternativă a Litigiilor" 
                                className="h-10 object-contain grayscale hover:grayscale-0 transition-all duration-200"
                            />
                        </a>
                        <Link to="/termeni-si-conditii" className="hover:text-stone-200 transition-colors">
                            {t('footer.terms')}
                        </Link>
                        <Link to="/politica-confidentialitate" className="hover:text-stone-200 transition-colors">
                            {t('footer.privacy')}
                        </Link>
                        <a
                            href="https://www.pluriel.ro/ro"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="hover:text-white text-stone-400 transition-colors duration-200"
                        >
                            Designed by PLURIEL Brand & Digital
                        </a>
                    </div>
                </div>
            </div>
        </footer>
    );
};

export default Footer;
